import { cancelSubscription, swapProductInSubscription, checkIfEligibleForDiscount, setDiscountLevel } from '../subscription-modal/ordergroove-api-helper';

const getFrequencyUnit = (period) => {
    switch (period) {
        case 1: return 'Day';
        case 2: return 'Week';
        case 3: return 'Month';
        case 4: return 'Year';
        default: return 'Month'; // Default to 'Month' if period is not recognized
    }
};

export default function accountCancelationFlow() {
    return {
      step: 'start',
      cancelationReasons: window.dsc.cancelationReasons?.reasons || [],
      showProductSwap: true,
      showSkipOrder: false,
      swapableProducts: [],
      selectedSwapProduct: null,
      currentItem: {},
      originalItem: {},
      loadingDiscount: true,
      eligibleForDiscount: false,
      discountLevel: null,
      selectedReason: null,
      selectedReasonText: '',
      otherReasonTouched: false,
      currencyCode: 'USD',
      processingSwap: false,
      processingDiscount: false,
      stepTitles: window.translations.customer.stepTitles,
      get canSubmitCancellation() {
        // Must have a reason selected
        if (!this.selectedReason) return false;

        // If "Other" is selected, must have text in the textbox
        const reasonObj = this.cancelationReasons.find((r) => r.value === this.selectedReason);
        if (reasonObj?.label === 'Other') {
          return this.selectedReasonText && this.selectedReasonText.trim().length > 0;
        }

        // For all other reasons, just need selection
        return true;
      },
      handleSelectedItem(item, step, shopID) {
        this.currentItem = item;
        this.originalItem = JSON.parse(JSON.stringify(item)); // Deep copy for a non-reactive snapshot
        this.swapableProducts = item.productObj.swappableItems;
        const currentProd = this.currentItem?.product;
        if (currentProd) {
          this.swapableProducts = this.swapableProducts.filter((p) => p.external_product_id !== currentProd);
        }
        this.showProductSwap = this.swapableProducts.length > 0;
        this.step = step;
        this.currencyCode = item.currency_code;
        this.checkIfEligibleForDiscount(shopID);

        let contentShown = this.showProductSwap ? ['skip', 'swap'] : ['skip']; // Skip option is always shown

        window.dataLayer.push({
          event: 'dl_custom_clear_dsc_data',
          DSC: undefined,
        });
        window.dataLayer.push({
          event: 'dl_custom_sub_cancel_content_view',
          DSC: {
            subscription_id: this.currentItem?.subscription ?? this.currentItem?.public_id ?? null,
            item_name: this.currentItem?.productObj?.name ?? null,
            item_variant_id: this.currentItem?.product ?? null,
            quantity: this.currentItem?.quantity ?? null,
            sub_frequency: `${this.currentItem?.every} month${this.currentItem?.every > 1 ? 's' : ''}`,
            step: 1,
            content_shown: contentShown,
          },
        });
      },
      async checkIfEligibleForDiscount(shopID) {
        this.loadingDiscount = true;
        const response = await checkIfEligibleForDiscount(
          this.currentItem.subscription || this.currentItem.public_id,
          shopID,
        );
        if (response.success) {
          const responseDiscountLevel = Number(response.discount_level); // Format to number to avoid issues with 50 vs "50.00"
          if (responseDiscountLevel !== 0) {
            this.eligibleForDiscount = true;
            this.discountType = response.discount_type;
            this.discountLevel = responseDiscountLevel;
          }
        }
        this.loadingDiscount = false;
      },
      async setDiscountLevel(shopID) {
        this.processingDiscount = true;

        // Fire data layer event
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'dl_custom_clear_dsc_data',
          DSC: undefined,
        });
        window.dataLayer.push({
          event: 'dl_custom_sub_cancel_discount_claim',
          DSC: {
            subscription_id: this.originalItem?.subscription ?? this.originalItem?.public_id ?? null,
            item_name: this.originalItem?.productObj?.name ?? null,
            item_variant_id: this.originalItem?.product ?? null,
            quantity: this.originalItem?.quantity ?? null,
            sub_frequency:
              this.originalItem?.frequency_text ??
              `${this.originalItem?.every} ${getFrequencyUnit(this.originalItem?.every_period)}${this.originalItem?.every > 1 ? 's' : ''}`,
            discount_type: this.discountType ?? 'percentage',
            discount_amount: this.discountLevel,
            ship_date: this.originalItem?.placeDateRaw ?? this.originalItem?.place ?? null,
          },
        });

        try {
          const response = await setDiscountLevel(
            this.currentItem.subscription || this.currentItem.public_id,
            this.discountLevel,
            shopID,
          );
          if (response.success) {
            const productObject = this.currentItem?.productObj;
            this.$dispatch('toast', { type: 'success', product: productObject, message: window.translations?.customer?.global?.discounted_next_order });
            this.$dispatch('order-updated', { detail: { isDateLoading: false } });
            this.closeModal('account-cancelation-flow-modal');
            this.step = 'start';
          }
        } catch (error) {
          console.error('Error setting discount level:', error);
          this.$dispatch('toast', { type: 'error', message: window.translations.customer.alerts.discount_apply_failed });
        } finally {
          this.processingDiscount = false;
        }
      },
      proceedToSwap() {
        // Fire data layer event
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'dl_custom_clear_dsc_data',
          DSC: undefined,
        });
        window.dataLayer.push({
          event: 'dl_custom_sub_cancel_swap_click',
          DSC: {
            subscription_id: this.originalItem?.subscription ?? this.originalItem?.public_id ?? null,
            item_name: this.originalItem?.productObj?.name ?? null,
            item_variant_id: this.originalItem?.product ?? null,
            quantity: this.originalItem?.quantity ?? null,
            sub_frequency:
              this.originalItem?.frequency_text ??
              `${this.originalItem?.every} ${getFrequencyUnit(this.originalItem?.every_period)}${this.originalItem?.every > 1 ? 's' : ''}`,
            ship_date: this.originalItem?.placeDateRaw ?? this.originalItem?.place ?? null,
          },
        });

        // Set the step
        this.step = 'swap';
      },
      proceedToDiscount() {
        // Fire data layer events
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'dl_custom_clear_dsc_data',
          DSC: undefined,
        });
        window.dataLayer.push({
          event: 'dl_custom_sub_cancel_click_2nd',
          DSC: {
            subscription_id: this.originalItem?.subscription ?? this.originalItem?.public_id ?? null,
            item_name: this.originalItem?.productObj?.name ?? null,
            item_variant_id: this.originalItem?.product ?? null,
            quantity: this.originalItem?.quantity ?? null,
            sub_frequency:
              this.originalItem?.frequency_text ??
              `${this.originalItem?.every} ${getFrequencyUnit(this.originalItem?.every_period)}${this.originalItem?.every > 1 ? 's' : ''}`,
          },
        });

        // If user will be shown discount option, fire second event for content view
        if (this.eligibleForDiscount) {
          window.dataLayer.push({
            event: 'dl_custom_clear_dsc_data',
            DSC: undefined,
          });
          window.dataLayer.push({
            event: 'dl_custom_sub_cancel_content_view',
            DSC: {
              subscription_id: this.originalItem?.subscription ?? this.originalItem?.public_id ?? null,
              item_name: this.originalItem?.productObj?.name ?? null,
              item_variant_id: this.originalItem?.product ?? null,
              quantity: this.originalItem?.quantity ?? null,
              sub_frequency:
                this.originalItem?.frequency_text ??
                `${this.originalItem?.every} ${getFrequencyUnit(this.originalItem?.every_period)}${this.originalItem?.every > 1 ? 's' : ''}`,
              step: 2,
              content_shown: ['discount'],
              discount_type: this.discountType ?? 'percentage',
              discount_amount: this.discountLevel,
            },
          });
        } else {
          window.dataLayer.push({
            event: 'dl_custom_clear_dsc_data',
            DSC: undefined,
          });
          window.dataLayer.push({
            event: 'dl_custom_sub_cancel_content_view',
            DSC: {
              subscription_id: this.originalItem?.subscription ?? this.originalItem?.public_id ?? null,
              item_name: this.originalItem?.productObj?.name ?? null,
              item_variant_id: this.originalItem?.product ?? null,
              quantity: this.originalItem?.quantity ?? null,
              sub_frequency:
                this.originalItem?.frequency_text ??
                `${this.originalItem?.every} ${getFrequencyUnit(this.originalItem?.every_period)}${this.originalItem?.every > 1 ? 's' : ''}`,
              step: 2,
              content_shown: ['are_you_sure'],
            },
          });
        }

        // Set the step
        this.step = 'discount';
      },

      proceedToCancellationSurvey() {
        // 1. Fire event to track cancel click.
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'dl_custom_clear_dsc_data',
          DSC: undefined,
        });
        window.dataLayer.push({
          event: 'dl_custom_sub_cancel_click_3rd',
          DSC: {
            subscription_id: this.originalItem?.subscription ?? this.originalItem?.public_id ?? null,
            item_name: this.originalItem?.productObj?.name ?? null,
            item_variant_id: this.originalItem?.product ?? null,
            quantity: this.originalItem?.quantity ?? null,
            sub_frequency:
              this.originalItem?.frequency_text ??
              `${this.originalItem?.every} ${getFrequencyUnit(this.originalItem?.every_period)}${this.originalItem?.every > 1 ? 's' : ''}`,
          },
        });
        window.dataLayer.push({
          event: 'dl_custom_clear_dsc_data',
          DSC: undefined,
        });
        window.dataLayer.push({
          event: 'dl_custom_sub_cancel_content_view',
          DSC: {
            subscription_id: this.originalItem?.subscription ?? this.originalItem?.public_id ?? null,
            item_name: this.originalItem?.productObj?.name ?? null,
            item_variant_id: this.originalItem?.product ?? null,
            quantity: this.originalItem?.quantity ?? null,
            sub_frequency:
              this.originalItem?.frequency_text ??
              `${this.originalItem?.every} ${getFrequencyUnit(this.originalItem?.every_period)}${this.originalItem?.every > 1 ? 's' : ''}`,
            step: 3,
            content_shown: ['reasons'],
          },
        });

        // 2. Move to the survey step.
        this.step = 'reason';
      },
      async submitCancellation() {
        // 1. Fire event to track cancel reason submission click.
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'dl_custom_clear_dsc_data',
          DSC: undefined,
        });
        window.dataLayer.push({
          event: 'dl_custom_sub_cancel_reason_submit',
          DSC: {
            subscription_id: this.originalItem?.subscription ?? this.originalItem?.public_id ?? null,
            item_name: this.originalItem?.productObj?.name ?? null,
            item_variant_id: this.originalItem?.product ?? null,
            quantity: this.originalItem?.quantity ?? null,
            sub_frequency:
              this.originalItem?.frequency_text ??
              `${this.originalItem?.every} ${getFrequencyUnit(this.originalItem?.every_period)}${this.originalItem?.every > 1 ? 's' : ''}`,
            ship_date: this.originalItem?.placeDateRaw ?? this.originalItem?.place ?? null,
          },
        });

        // 2. Prepare the cancellation reason.
        if (!this.selectedReason) {
          this.selectedReason = '1';
        }
        let submitReason = this.selectedReason;
        const reasonLabel = this.cancelationReasons.find((r) => r.value === this.selectedReason)?.label;
        if (reasonLabel === 'Other') {
          submitReason = `${this.selectedReason} | ${this.selectedReasonText}`;
        }

        // 3. Cancel the subscription with the correct reason.
        const cancelResponse = await cancelSubscription(
          this.currentItem.subscription || this.currentItem.public_id,
          submitReason,
        );
        if (cancelResponse.success) {
          // 4. Show the confirmation toast now that cancellation is confirmed.
          const productObject = this.currentItem?.productObj;
          this.$dispatch('toast', { type: 'success', product: productObject, message: window.translations?.customer?.global?.subscription_canceled });
          this.$dispatch('order-updated', { detail: { isDateLoading: false } });
          this.$dispatch('update-subscriptions-when-remove', { subscription: this.currentItem });

          // 5. Reset the modal state.
          this.step = 'start';
          this.selectedReason = null;
          this.selectedReasonText = '';
        } else {
          // Handle cancellation failure
          this.$dispatch('toast', { type: 'error', message: window.translations.customer.alerts.subscription_cancel_failed });
        }
      },
      resetModal() {
        if (!this.processingSwap) {
          this.selectedSwapProduct = null;
        }
        this.selectedReason = null;
        this.selectedReasonText = '';
        this.step = 'start';
      },
      async skipOrder() {
        this.$dispatch('change-subscription-delivery-date', {
          subscriptionId: this.currentItem.subscription || this.currentItem.public_id,
          currentDeliveryDate: this.currentItem.place || this.currentItem.placeDateRaw,
          context: 'skip',
        });
        this.closeModal('account-cancelation-flow-modal');
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'dl_custom_clear_dsc_data',
          DSC: undefined,
        });
        window.dataLayer.push({
          event: 'dl_custom_sub_cancel_skip_confirm',
          DSC: {
            subscription_id: this.currentItem?.subscription ?? this.currentItem?.public_id ?? null,
            item_name: this.currentItem?.productObj?.name ?? null,
            item_variant_id: this.currentItem?.product ?? null,
            quantity: this.currentItem?.quantity ?? null,
            sub_frequency:
              this.currentItem?.frequency_text ??
              `${this.currentItem?.every} ${getFrequencyUnit(this.currentItem?.every_period)}${this.currentItem?.every > 1 ? 's' : ''}`,
            ship_date: this.currentItem?.placeDateRaw ?? this.currentItem?.place ?? null,
          },
        });
      },
      async swapProduct() {
        this.processingSwap = true;
        const response = await swapProductInSubscription(
          this.currentItem.subscription || this.currentItem.public_id,
          this.selectedSwapProduct.external_product_id,
        );
        if (response.success) {
          this.$dispatch('update-swapped-product', {
            subscriptionId: this.currentItem.subscription || this.currentItem.public_id,
            newProduct: this.selectedSwapProduct,
          });
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'dl_custom_clear_dsc_data',
            DSC: undefined,
          });
          window.dataLayer.push({
            event: 'dl_custom_sub_cancel_swap_confirm',
            DSC: {
              subscription_id: this.originalItem?.subscription ?? this.originalItem?.public_id ?? null,
              item_name: this.originalItem?.productObj?.name ?? null,
              item_variant_id: this.originalItem?.product ?? null,
              quantity: this.originalItem?.quantity ?? null,
              sub_frequency:
                this.originalItem?.frequency_text ??
                `${this.originalItem?.every} ${getFrequencyUnit(this.originalItem?.every_period)}${this.originalItem?.every > 1 ? 's' : ''}`,
              new_item_variant_id: this.selectedSwapProduct?.external_product_id,
              ship_date: this.originalItem?.placeDateRaw ?? this.originalItem?.place ?? null,
            },
          });
          this.step = 'start';
          this.swapableProducts = [];
          this.selectedSwapProduct = null;
          this.processingSwap = false;
          this.closeModal('account-cancelation-flow-modal');
          this.$dispatch('toast', {
            message: window.translations.customer.global.product_swapped,
            type: 'success',
            product: this.selectedSwapProduct,
          });
        }
      },
    };
}
